/**
 * useClientPermissions Composable
 *
 * Provides permission checking for client portal users.
 * Reads the current user's `permissionLevel` from their companies[] entry
 * for the active company and exposes computed booleans for UI gating.
 *
 * Admin/super users and impersonating admins bypass all restrictions.
 */

import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useUserAuthStore } from '@/stores/userAuthStore'
import { useViewModeStore } from '@/stores/viewModeStore'
import { useClientCompaniesStore } from '@/stores/clientCompaniesStore'
import { CLIENT_PERMISSION_LEVELS } from '@/constants/clientPermissions'
import { isAdminUser } from '@/utils/userUtils'

export function useClientPermissions() {
  const route = useRoute()
  const userAuthStore = useUserAuthStore()
  const viewModeStore = useViewModeStore()
  const clientCompaniesStore = useClientCompaniesStore()

  /**
   * Resolve the effective permission level for the current user + company.
   * Returns 'administrator' for admin/super users, impersonating users,
   * or when no permissionLevel is set (backward compatible).
   */
  const permissionLevel = computed(() => {
    const user = userAuthStore.user
    if (!user) return CLIENT_PERMISSION_LEVELS.ADMINISTRATOR

    // Admin/super users always get full access
    if (isAdminUser(user)) {
      return CLIENT_PERMISSION_LEVELS.ADMINISTRATOR
    }

    // Impersonating admins get full access
    if (viewModeStore.isImpersonating) {
      return CLIENT_PERMISSION_LEVELS.ADMINISTRATOR
    }

    // Find the company entry for the current route's company
    const companyId = route.params.id || user.activeCompanyId || user.companyId
    if (!companyId || !user.companies?.length) {
      return CLIENT_PERMISSION_LEVELS.ADMINISTRATOR
    }

    const companyEntry = user.companies.find(c => {
      if (typeof c === 'string') return c === companyId
      return c.companyId === companyId
    })

    if (!companyEntry || typeof companyEntry === 'string') {
      return CLIENT_PERMISSION_LEVELS.ADMINISTRATOR
    }

    // Default undefined/null to administrator for backward compatibility
    return companyEntry.permissionLevel || CLIENT_PERMISSION_LEVELS.ADMINISTRATOR
  })

  const isViewOnly = computed(() => permissionLevel.value === CLIENT_PERMISSION_LEVELS.VIEW_ONLY)
  const isManager = computed(() => permissionLevel.value === CLIENT_PERMISSION_LEVELS.MANAGER)
  const isAgency = computed(() => permissionLevel.value === CLIENT_PERMISSION_LEVELS.AGENCY)
  const isAdministrator = computed(() => permissionLevel.value === CLIENT_PERMISSION_LEVELS.ADMINISTRATOR)

  // Company-level feature restriction (not bypassed by admin/impersonation)
  const isVysualOnly = computed(() => {
    const companyId = route.params.id
    if (!companyId) return false
    const company = clientCompaniesStore.getCompanyById(companyId)
    return !!company?.vysualOnly
  })

  // Feature access booleans
  const canAccessUpload = computed(() => !isViewOnly.value)
  const canAccessSharing = computed(() => true)
  const canAccessProjects = computed(() => !isViewOnly.value && !isVysualOnly.value)
  // Starting an edit project is a project action — vysual-only companies cannot
  const canCreateEditProject = computed(() => !isViewOnly.value && !isVysualOnly.value)
  const canAccessCalendar = computed(() => !isViewOnly.value && !isVysualOnly.value)
  const canAccessReviews = computed(() => !isViewOnly.value && !isVysualOnly.value)
  // Managers and agency staff reach the company page for the Users tab only —
  // CompanyDetails.vue filters storage, contracts and billing back out.
  const canAccessCompany = computed(() => !isViewOnly.value)
  // ...and only administrators may change the company itself.
  const canEditCompanySettings = computed(() => isAdministrator.value)
  // Inviting people, changing their level, removing them. Capped by
  // ASSIGNABLE_LEVELS and canActOn at the call site: nobody may act on, or
  // grant, a level above their own.
  const canManageUsers = computed(() => !isViewOnly.value)
  const canUseBulkTools = computed(() => !isViewOnly.value)
  const canBulkShare = computed(() => true)
  const canShareAsset = computed(() => true)
  const canEditAsset = computed(() => !isViewOnly.value)
  // Replacing the file and deleting the asset. Managers and administrators
  // both qualify — a manager has full use of the account, and these are asset
  // actions, not company settings. Kept separate from canEditAsset so
  // loosening metadata editing later can't quietly loosen destruction too.
  const canManageAssetFile = computed(() => !isViewOnly.value)
  const canAccessSettings = computed(() => !isViewOnly.value)
  const canAccessShows = computed(() => !isViewOnly.value && !isVysualOnly.value)
  // Company feature flag + any level above view-only.
  const canAccessConversations = computed(() => {
    const companyId = route.params.id
    if (!companyId || isViewOnly.value) return false
    return !!clientCompaniesStore.getCompanyById(companyId)?.conversationsEnabled
  })

  return {
    permissionLevel,
    isViewOnly,
    isManager,
    isAgency,
    isAdministrator,
    isVysualOnly,
    canAccessUpload,
    canAccessSharing,
    canAccessProjects,
    canCreateEditProject,
    canAccessCalendar,
    canAccessReviews,
    canAccessCompany,
    canEditCompanySettings,
    canManageUsers,
    canUseBulkTools,
    canBulkShare,
    canShareAsset,
    canEditAsset,
    canManageAssetFile,
    canAccessSettings,
    canAccessShows,
    canAccessConversations
  }
}
